import { getApiBaseUrl } from '../../config/region';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  XMarkIcon, 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  StarIcon,
  HeartIcon,
  ClockIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ChatBubbleLeftIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import ImageUpload from '../ImageUpload/ImageUpload';

// ── Role Switcher (ADMIN/OWNER only) ──────────────────────────────────
const RoleSwitcher = ({ currentRole, onRoleChange }) => {
  const { t } = useTranslation();
  const AVAILABLE_ROLES = [
    { value: 'ADMIN',      label: t('profile.roleAdminLabel'),      desc: t('profile.roleAdminDesc') },
    { value: 'OWNER',      label: t('profile.roleOwnerLabel'),      desc: t('profile.roleOwnerDesc') },
    { value: 'TRANSLATOR', label: t('profile.roleTranslatorLabel'), desc: t('profile.roleTranslatorDesc') },
    { value: 'MANAGER',    label: t('profile.roleManagerLabel'),    desc: t('profile.roleManagerDesc') },
    { value: 'CONSUMER',   label: t('profile.roleConsumerLabel'),   desc: t('profile.roleConsumerDesc') },
  ];
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(currentRole);

  const handleSwitch = async (role) => {
    if (role === currentRole) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('venue_app_token');
      const res = await fetch(`${getApiBaseUrl()}/users/me/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ role }),
      });
      const data = await res.json();
      if (res.ok) {
        setSelected(role);
        onRoleChange(role);
      } else {
        alert(data.message || t('profile.roleSwitchFailed'));
      }
    } catch (err) {
      console.error('Role switch error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h3 style={{
        fontSize: 18, fontWeight: 600, color: '#1f2937',
        marginBottom: 12, margin: 0,
      }}>
        {t('profile.roleSwitcherTitle')}
      </h3>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px 0' }}>
        {t('profile.roleSwitcherDesc')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {AVAILABLE_ROLES.map((r) => {
          const isActive = selected === r.value;
          return (
            <button
              key={r.value}
              onClick={() => handleSwitch(r.value)}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', borderRadius: 10,
                border: isActive ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                background: isActive ? '#fffbeb' : '#fff',
                cursor: saving ? 'wait' : 'pointer',
                opacity: saving && !isActive ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{
                  fontWeight: 600, fontSize: 14,
                  color: isActive ? '#b45309' : '#374151',
                }}>
                  {r.label}
                  {isActive && <span style={{ marginLeft: 8, fontSize: 12, color: '#f59e0b' }}>{t('profile.roleActive')}</span>}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{r.desc}</div>
              </div>
              {isActive && (
                <div style={{
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#f59e0b', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700,
                }}>
                  ✓
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ProfileScreen = ({ isOpen, onClose }) => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [userStats, setUserStats] = useState({
    venuesVisited: 0,
    reviewsGiven: 0,
    favorites: 0,
    hoursSaved: 0
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    profileImage: '',
    referralCode: '',
    referredBy: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // My Reviews state
  const [myReviews, setMyReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [hasMoreReviews, setHasMoreReviews] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Fetch current user data on open
  useEffect(() => {
    if (!isOpen) return;

    const token = localStorage.getItem('venue_app_token');
    if (!token) return;

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);
    myHeaders.append("x-region", "th"); // ✅ PHASE 4: Fixed region from india to th

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    };

    // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
    fetch(`${getApiBaseUrl()}/users/me`, requestOptions)
      .then(res => res.json())
      .then(result => {
        setUser(result);
        setFormData({
          name: result.name || '',
          phone: result.phone || '',
          email: result.email || '',
          address: result.address || '',
          profileImage: result.profileImage || '',
          referralCode: result.referralCode || '',
          referredBy: result.referredBy || ''
        });
        // Load user stats
        const stats = {
          venuesVisited: parseInt(localStorage.getItem('user_venues_visited') || '0'),
          reviewsGiven: parseInt(localStorage.getItem('user_reviews_given') || '0'),
          favorites: parseInt(localStorage.getItem('user_favorites') || '0'),
          hoursSaved: parseInt(localStorage.getItem('user_hours_saved') || '0')
        };
        setUserStats(stats);
      })
      .catch(err => {
        console.error(err);
        setError(t('common.error'));
      });
  }, [isOpen]);

  // Fetch user's reviews
  const fetchMyReviews = (page = 1) => {
    setReviewsLoading(true);
    const token = localStorage.getItem('venue_app_token');
    if (!token) return;

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);
    myHeaders.append("x-region", "th"); // ✅ PHASE 4: Fixed region from india to th

    // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
    fetch(`${getApiBaseUrl()}/reviews/my-reviews?page=${page}&limit=5`, {
      method: "GET",
      headers: myHeaders,
    })
      .then(res => res.json())
      .then(result => {
        if (page === 1) {
          setMyReviews(result.reviews || []);
        } else {
          setMyReviews(prev => [...prev, ...(result.reviews || [])]);
        }
        setHasMoreReviews(result.pagination?.hasMore || false);
        setReviewsLoading(false);
        
        // Update stats
        if (result.pagination?.totalReviews) {
          localStorage.setItem('user_reviews_given', result.pagination.totalReviews.toString());
          setUserStats(prev => ({ ...prev, reviewsGiven: result.pagination.totalReviews }));
        }
      })
      .catch(err => {
        console.error(err);
        setReviewsLoading(false);
      });
  };

  // Delete review
  const deleteReview = (reviewId) => {
    if (!window.confirm(t('profile.deleteReviewConfirm'))) return;

    const token = localStorage.getItem('venue_app_token');
    if (!token) return;

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);
    myHeaders.append("x-region", "th"); // ✅ PHASE 4: Fixed region from india to th

    // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
    fetch(`${getApiBaseUrl()}/reviews/${reviewId}`, {
      method: "DELETE",
      headers: myHeaders,
    })
      .then(res => res.json())
      .then(() => {
        setMyReviews(prev => prev.filter(r => r._id !== reviewId));
        setUserStats(prev => ({ ...prev, reviewsGiven: Math.max(0, prev.reviewsGiven - 1) }));
      })
      .catch(err => console.error(err));
  };

  // Fetch reviews when modal opens
  useEffect(() => {
    if (isOpen && user) {
      fetchMyReviews(1);
      setReviewsPage(1);
    }
  }, [isOpen, user]);

  // Check if field should be disabled based on login method
  const isFieldDisabled = (fieldName) => {
    if (!user?.loginMethod) return false;
    
    if (user.loginMethod === 'phone' && fieldName === 'phone') {
      return true;
    }
    
    if (user.loginMethod === 'email' && fieldName === 'email') {
      return true;
    }
    
    if (user.loginMethod === 'google' && fieldName === 'email') {
      return true;
    }
    
    return false;
  };

  // Handle form input changes
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(d => ({ ...d, [name]: value }));
  };

  // Handle image update from ImageUpload component
  const handleImageUpdate = (imageUrl) => {
    setFormData(d => ({ ...d, profileImage: imageUrl }));
    setUser(prev => ({ ...prev, profileImage: imageUrl }));
  };

  // Save profile update with enhanced error handling
  const handleSave = () => {
    setIsSaving(true);
    setError(null);

    const token = localStorage.getItem('venue_app_token');
    if (!token) {
      setError(t('profile.notAuthenticated'));
      setIsSaving(false);
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("x-region", "th"); // ✅ PHASE 4: Fixed region from india to th

    // Create a copy of formData and remove fields that shouldn't be updated
    const dataToSend = { ...formData };
    
    // Remove phone if user logged in via phone
    if (user?.loginMethod === 'phone') {
      delete dataToSend.phone;
    }
    
    // Remove email if user logged in via email or Google
    if (user?.loginMethod === 'email' || user?.loginMethod === 'google') {
      delete dataToSend.email;
    }

    // Remove profileImage from here since it's handled by the upload component
    delete dataToSend.profileImage;

    // Remove empty strings and null values
    Object.keys(dataToSend).forEach(key => {
      if (dataToSend[key] === '' || dataToSend[key] === null || dataToSend[key] === undefined) {
        delete dataToSend[key];
      }
    });

    // If no fields to update, don't make the request
    if (Object.keys(dataToSend).length === 0) {
      setIsSaving(false);
      setError(t('profile.noChanges'));
      setTimeout(() => setError(null), 2000);
      return;
    }

    const raw = JSON.stringify(dataToSend);

    const requestOptions = {
      method: 'PUT',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
    fetch(`${getApiBaseUrl()}/users/me`, requestOptions)
      .then(res => {
        if (!res.ok) {
          return res.json().then(data => {
            throw new Error(data.message || t('profile.updateFailed'));
          });
        }
        return res.json();
      })
      .then(result => {
        setUser(result.user || result);
        setIsSaving(false);
        setError(null);
      })
      .catch(err => {
        console.error(err);
        setError(err.message || t('profile.updateFailed'));
        setIsSaving(false);
      });
  };

  if (!isOpen) return null;

  const getAuthMethodIcon = () => {
    switch (user?.loginMethod) {
      case 'phone':
        return DevicePhoneMobileIcon;
      case 'email':
        return EnvelopeIcon;
      case 'google':
        return ShieldCheckIcon;
      default:
        return ShieldCheckIcon;
    }
  };

  const getAuthMethodLabel = () => {
    switch (user?.loginMethod) {
      case 'phone':
        return t('profile.phoneAuth');
      case 'email':
        return t('profile.emailAuth');
      case 'google':
        return t('profile.googleAuth');
      default:
        return t('profile.authenticated');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto',
            backdropFilter: 'blur(4px)'
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{
              backgroundColor: 'white',
              borderRadius: 20,
              width: '100%',
              maxWidth: 500,
              maxHeight: 'calc(100vh - 40px)',
              marginTop: '20px',
              marginBottom: '20px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b, #ea580c)',
              padding: 24,
              position: 'relative',
              overflow: 'hidden',
              color: 'white',
              textAlign: 'center',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              flexShrink: 0
            }}>
              {/* Background decorations */}
              <div style={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
              }} />
              <div style={{
                position: 'absolute',
                bottom: -30,
                left: -30,
                width: 60,
                height: 60,
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
              }} />

              {/* Close button */}
              <button
                onClick={onClose}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  zIndex: 10
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
              >
                <XMarkIcon style={{ height: 16, width: 16, color: 'white' }} />
              </button>

              {/* Profile header content */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: 20,
                position: 'relative',
                zIndex: 5
              }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 12,
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  overflow: 'hidden'
                }}>
                  {formData.profileImage ? (
                    <img 
                      src={formData.profileImage} 
                      alt="Profile" 
                      style={{ 
                        borderRadius: '50%', 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover' 
                      }}
                    />
                  ) : (
                    <UserIcon style={{ height: 40, width: 40, color: 'white' }} />
                  )}
                </div>
                <h2 style={{ 
                  fontSize: 24, 
                  fontWeight: 'bold', 
                  margin: 0, 
                  marginBottom: 4,
                  wordBreak: 'break-word'
                }}>
                  {formData.name || t('common.user')}
                </h2>
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.85)', 
                  fontSize: 14, 
                  margin: 0,
                  wordBreak: 'break-word'
                }}>
                  {formData.email || ''}
                </p>
                {user?.loginMethod && (
                  <div style={{
                    marginTop: 8,
                    padding: '4px 12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 20,
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    {React.createElement(getAuthMethodIcon(), { style: { height: 14, width: 14 } })}
                    {getAuthMethodLabel()}
                  </div>
                )}
              </div>
            </div>

            {/* Scrollable Content */}
            <div style={{ 
              flex: 1,
              overflowY: 'auto',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 24
            }}>
              {/* Personal Information Section */}
              <div>
                <h3 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#1f2937',
                  marginBottom: 16,
                  margin: 0
                }}>
                  {t('profile.personalInformation')}
                </h3>

                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 16
                }}>
                  <ImageUpload 
                    currentImage={formData.profileImage}
                    onImageUpdate={handleImageUpdate}
                    disabled={isSaving}
                  />
                  <LabeledInput
                    label={t('profile.labelName')}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('profile.namePlaceholder')}
                  />
                  <LabeledInput
                    label={t('profile.labelPhone')}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('profile.phonePlaceholder')}
                    disabled={isFieldDisabled('phone')}
                    lockedMessage={isFieldDisabled('phone') ? t('profile.lockedForLogin') : null}
                  />
                  <LabeledInput
                    label={t('profile.labelEmail')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('profile.emailPlaceholder')}
                    disabled={isFieldDisabled('email')}
                    lockedMessage={isFieldDisabled('email') ? t('profile.lockedForLogin') : null}
                  />
                  <LabeledInput
                    label={t('profile.labelAddress')}
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder={t('profile.addressPlaceholder')}
                  />
                  <LabeledInput
                    label={t('profile.labelReferralCode')}
                    name="referralCode"
                    value={formData.referralCode}
                    onChange={handleChange}
                    placeholder={t('profile.referralCodePlaceholder')}
                  />
                  <LabeledInput
                    label={t('profile.labelReferredBy')}
                    name="referredBy"
                    value={formData.referredBy}
                    onChange={handleChange}
                    placeholder={t('profile.referredByPlaceholder')}
                  />
                </div>
              </div>

              {/* Role Switcher — only for ADMIN/OWNER */}
              {user && ['ADMIN', 'OWNER'].includes(user.role) && (
                <RoleSwitcher currentRole={user.role} onRoleChange={(newRole) => setUser(prev => ({ ...prev, role: newRole }))} />
              )}

              {/* Activity Stats Section */}
              <div>
                <h3 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#1f2937',
                  marginBottom: 16,
                  margin: 0
                }}>
                  {t('profile.yourActivity')}
                </h3>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: 16
                }}>
                  {[
                    { label: t('profile.venuesVisited'), value: userStats.venuesVisited.toString(), icon: MapPinIcon, color: '#f59e0b' },
                    { label: t('profile.reviewsGiven'), value: userStats.reviewsGiven.toString(), icon: StarIcon, color: '#ea580c' },
                    { label: t('profile.favorites'), value: userStats.favorites.toString(), icon: HeartIcon, color: '#dc2626' },
                    { label: t('profile.hoursSaved'), value: userStats.hoursSaved.toString(), icon: ClockIcon, color: '#059669' }
                  ].map((stat, i) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      style={{
                        padding: 16,
                        backgroundColor: '#f9fafb',
                        borderRadius: 12,
                        textAlign: 'center',
                        border: `2px solid ${stat.color}33`,
                        boxShadow: `0 4px 12px ${stat.color}22`,
                        minHeight: 120
                      }}
                    >
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: `${stat.color}33`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px auto'
                      }}>
                        <stat.icon style={{ height: 24, width: 24, color: stat.color }} />
                      </div>
                      <p style={{ 
                        margin: 0, 
                        fontSize: 24, 
                        fontWeight: 'bold', 
                        color: stat.color,
                        marginBottom: 4
                      }}>
                        {stat.value}
                      </p>
                      <p style={{ 
                        margin: 0, 
                        fontSize: 12, 
                        color: '#6b7280',
                        lineHeight: '1.2'
                      }}>
                        {stat.label}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* My Reviews Section */}
              <div>
                <h3 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#1f2937',
                  marginBottom: 16,
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <ChatBubbleLeftIcon style={{ height: 20, width: 20, color: '#ea580c' }} />
                  {t('profile.myReviews')} ({userStats.reviewsGiven})
                </h3>

                {reviewsLoading && myReviews.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                    {t('profile.loadingReviews')}
                  </div>
                ) : myReviews.length === 0 ? (
                  <div style={{
                    padding: 40,
                    backgroundColor: '#f9fafb',
                    borderRadius: 12,
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <ChatBubbleLeftIcon style={{ height: 48, width: 48, color: '#d1d5db', margin: '0 auto 12px' }} />
                    <p style={{ margin: 0 }}>{t('profile.noReviews')}</p>
                    <p style={{ margin: '4px 0 0 0', fontSize: 14 }}>{t('profile.startExploring')}</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {myReviews.map((review) => (
                      <motion.div
                        key={review._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => {
                          if (review.venue || review.venueDetails?._id) {
                            const venueId = review.venue || review.venueDetails._id;
                            navigate(`/venue/${venueId}`);
                          }
                        }}
                        style={{
                          padding: 16,
                          backgroundColor: '#f9fafb',
                          borderRadius: 12,
                          border: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {/* Venue Name */}
                        {review.venueDetails && (
                          <div style={{ marginBottom: 8 }}>
                            <h4 style={{ 
                              margin: 0, 
                              fontSize: 16, 
                              fontWeight: 600, 
                              color: '#1f2937',
                              marginBottom: 4
                            }}>
                              {review.venueDetails.name}
                            </h4>
                            <p style={{ 
                              margin: 0, 
                              fontSize: 12, 
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              <MapPinIcon style={{ height: 12, width: 12 }} />
                              {review.venueDetails.city}, {review.venueDetails.country}
                            </p>
                          </div>
                        )}

                        {/* Rating */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                          {[...Array(5)].map((_, i) => (
                            <StarIcon
                              key={i}
                              style={{
                                height: 16,
                                width: 16,
                                fill: i < review.rating ? '#f59e0b' : 'none',
                                stroke: i < review.rating ? '#f59e0b' : '#d1d5db'
                              }}
                            />
                          ))}
                          <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 4 }}>
                            {new Date(review.createdAt).toLocaleDateString()}
                          </span>
                        </div>

                        {/* Review Title */}
                        {review.title && (
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: 14, 
                            fontWeight: 600, 
                            color: '#374151' 
                          }}>
                            {review.title}
                          </p>
                        )}

                        {/* Review Comment */}
                        {review.comment && (
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: 14, 
                            color: '#6b7280',
                            lineHeight: 1.5
                          }}>
                            {review.comment}
                          </p>
                        )}

                        {/* Photos */}
                        {review.photos && review.photos.length > 0 && (
                          <div style={{ 
                            display: 'flex', 
                            gap: 8, 
                            flexWrap: 'wrap',
                            marginBottom: 8
                          }}>
                            {review.photos.slice(0, 3).map((photo, idx) => (
                              <img
                                key={idx}
                                src={photo}
                                alt={t('review.photoAlt', { number: idx + 1 })}
                                style={{
                                  width: 60,
                                  height: 60,
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                  border: '1px solid #e5e7eb'
                                }}
                              />
                            ))}
                            {review.photos.length > 3 && (
                              <div style={{
                                width: 60,
                                height: 60,
                                borderRadius: 8,
                                backgroundColor: '#e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#6b7280'
                              }}>
                                +{review.photos.length - 3}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: 8,
                          borderTop: '1px solid #e5e7eb'
                        }}>
                          <div style={{ 
                            fontSize: 12, 
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            <span>{t('profile.helpful', { count: review.helpful })}</span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteReview(review._id);
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#fef2f2',
                              color: '#dc2626',
                              border: '1px solid #fecaca',
                              borderRadius: 8,
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fee2e2'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          >
                            <TrashIcon style={{ height: 14, width: 14 }} />
                            {t('profile.deleteReview')}
                          </button>
                        </div>
                      </motion.div>
                    ))}

                    {/* Load More Button */}
                    {hasMoreReviews && (
                      <button
                        onClick={() => {
                          const nextPage = reviewsPage + 1;
                          setReviewsPage(nextPage);
                          fetchMyReviews(nextPage);
                        }}
                        disabled={reviewsLoading}
                        style={{
                          padding: 12,
                          backgroundColor: '#f9fafb',
                          color: '#ea580c',
                          border: '1px solid #e5e7eb',
                          borderRadius: 12,
                          fontSize: 14,
                          fontWeight: 500,
                          cursor: reviewsLoading ? 'wait' : 'pointer'
                        }}
                      >
                        {reviewsLoading ? t('common.loading') : t('profile.loadMoreReviews')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    padding: 12,
                    backgroundColor: '#fef2f2',
                    color: '#b91c1c',
                    borderRadius: 12,
                    textAlign: 'center',
                    border: '1px solid #fecaca'
                  }}
                >
                  {error}
                </motion.div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  width: '100%',
                  padding: 16,
                  backgroundColor: isSaving ? '#9ca3af' : '#ea580c',
                  color: 'white',
                  fontWeight: '600',
                  borderRadius: 12,
                  border: 'none',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  boxShadow: isSaving ? 'none' : '0 4px 12px rgba(234, 88, 12, 0.3)',
                  transition: 'all 0.3s ease',
                  fontSize: 16
                }}
                onMouseEnter={e => !isSaving && (e.currentTarget.style.backgroundColor = '#f97316')}
                onMouseLeave={e => !isSaving && (e.currentTarget.style.backgroundColor = '#ea580c')}
              >
                {isSaving ? t('profile.saving') : t('profile.saveChanges')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Helper component for labeled input
const LabeledInput = ({ label, name, value, onChange, placeholder, type = 'text', disabled = false, lockedMessage = null }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ 
      fontSize: 14, 
      fontWeight: 600, 
      color: disabled ? '#9ca3af' : '#374151',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 6
    }}>
      {label}
      {disabled && <LockClosedIcon style={{ height: 14, width: 14, color: '#9ca3af' }} />}
    </label>
    <input
      type={type}
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        padding: '12px 16px',
        borderRadius: 12,
        border: disabled ? '1px solid #e5e7eb' : '1px solid #d1d5db',
        fontSize: 14,
        outline: 'none',
        transition: 'border-color 0.2s',
        backgroundColor: disabled ? '#f9fafb' : 'white',
        color: disabled ? '#9ca3af' : '#000',
        cursor: disabled ? 'not-allowed' : 'text',
        boxSizing: 'border-box'
      }}
      onFocus={e => {
        if (!disabled) {
          e.target.style.borderColor = '#ea580c';
          e.target.style.boxShadow = '0 0 0 3px rgba(234, 88, 12, 0.1)';
        }
      }}
      onBlur={e => {
        if (!disabled) {
          e.target.style.borderColor = '#d1d5db';
          e.target.style.boxShadow = 'none';
        }
      }}
      autoComplete="off"
    />
    {lockedMessage && (
      <p style={{
        margin: 0,
        fontSize: 12,
        color: '#6b7280',
        fontStyle: 'italic'
      }}>
        {lockedMessage}
      </p>
    )}
  </div>
);

export default ProfileScreen;
